import {
  SellersFiltersComponent,
  SellersFilters,
} from "@/App/Sellers/Filters";
import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { TypePageActions } from "../Plans";
import { AddBtn } from "@/App/Sellers/Add";
import { TableData } from "@/App/Sellers/Table";
import { getSellersData } from "@/Api/sellers";
import { TypeUser } from "@/types/userType";
import { useAuth } from "@/hooks/auth";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { UserCheck } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

export type TableDataProps = {
  id: number;
  uuid: string;
  user_id: number;
  nickname?: string | null;
  phone: string;
  document: string;
  is_active: number;
  created_at: string;
  address: {
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
  };
  unity: {
    id: string;
    name: string;
  };
  user: {
    id: number;
    roles: {
      id: number;
      name: string;
    }[];
    uuid: string;
    name: string;
    photo: string;
    email: string;
    user_type_id: number;
    tenant_id: number;
    is_active: number;
  };
};

export type SellersSort = {
  order_by: "name" | "nickname";
  order_direction: "asc" | "desc";
};

export const PageList = () => {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const user = useAuth();
  const { hasPermission } = usePermissions();
  const unityId = user?.user?.unity?.[0]?.id
    ? String(user.user.unity[0].id)
    : "";
  const isUnityResponsible =
    user?.user?.user_type_id == TypeUser.unity_responsible;
  // Usuário de unidade só enxerga a própria: filtro de unidade fixo nela
  const lockedUnityId = isUnityResponsible ? unityId : undefined;
  const baseFilters: SellersFilters = unityId ? { unity_id: unityId } : {};
  const [currentFilters, setCurrentFilters] =
    useState<SellersFilters>(baseFilters);
  const [sort, setSort] = useState<SellersSort>({
    order_by: "name",
    order_direction: "asc",
  });

  const fetchData = async (
    page: number = 1,
    filters: SellersFilters = currentFilters,
    currentSort: SellersSort = sort
  ) => {
    setState("loading");
    const { data } = await getSellersData(page, { ...filters, ...currentSort });
    setListData(data?.response);
    setState("production");
  };

  useEffect(() => {
    fetchData(1);
  }, []);

  const handleSort = (column: SellersSort["order_by"]) => {
    const newSort: SellersSort = {
      order_by: column,
      order_direction:
        sort.order_by === column && sort.order_direction === "asc"
          ? "desc"
          : "asc",
    };
    setSort(newSort);
    fetchData(1, currentFilters, newSort);
  };

  const handleClearFilters = () => {
    setCurrentFilters(baseFilters);
    fetchData(1, baseFilters);
  };

  const pageActions: TypePageActions = {
    next: () => {
      if (listData?.current_page < listData?.last_page) {
        fetchData(listData?.current_page + 1);
      }
    },
    previous: () => {
      if (listData?.current_page > 1) {
        fetchData(listData?.current_page - 1);
      }
    },
    lastPage: () => {
      if (listData?.current_page !== listData?.last_page) {
        fetchData(listData?.last_page);
      }
    },
  };

  return (
    <Main>
      <If condition={hasPermission("view-sellers-list")}>
        <PageHeader
          icon={UserCheck}
          title="Vendedores"
          description="Gerencie sua equipe de vendas"
        >
          <If condition={hasPermission("create-seller")}>
            <AddBtn updateFront={setListData} />
          </If>
        </PageHeader>
        <div className="flex flex-col gap-3">
          <SellersFiltersComponent
            filters={currentFilters}
            onFiltersChange={setCurrentFilters}
            onApplyFilters={() => fetchData(1)}
            onClearFilters={handleClearFilters}
            lockedUnityId={lockedUnityId}
          />
          {state === "loading" ? (
            <CreateLoaderTable cols={5} lines={6} />
          ) : (
            <TableData
              dataApi={listData}
              sort={sort}
              onSort={handleSort}
              pageActions={pageActions}
              updateFront={setListData}
            />
          )}
        </div>
      </If>
    </Main>
  );
};
